import { parse } from 'query-string';
import React, { ReactNode, useEffect, useState } from 'react';
import { always, cond, equals, omit } from 'ramda';
import { AnimatePresence } from 'framer-motion';
import { useDeepCompareEffect } from 'react-use';
import { Element } from 'react-scroll';
import { useInView } from 'react-intersection-observer';
import { useRouter } from 'next/router';

import { GRID_SIZE, GRID_SIZE_ORDER, HIDDEN_BOTTOM } from '@modules/artworks/artworks.constants';
import { useFetchArtworks, useSetCurrentPage } from '@hooks/useArtworks';
import { useArtworksRoute, useFetchGroupingArtworks } from '@hooks/useArtworks/useArtworks';
import { InfiniteLoader } from '@components/PLP/infiniteLoader';
import reportError from '@utils/reportError';
import { ArtworksPagination } from '@components/PLP/artworksPagination';
import { Artwork } from '@definitions/artworks.types';
import { renderWhenTrue } from '@utils/rendering';
import { usePersist } from '@hooks/usePersist';
import { useFilters } from '@hooks/useFilters';
import { hasEmptyFilters } from '@utils/helpers';
import { getCurrentPLP } from '@modules/artworks/artworks.helpers';
import { NoResults } from '@components/noResults';
import { useFetchTradeArtworks } from '@hooks/useArtworks/useArtworks';

import { LargeGrid, MediumGrid, SmallGrid } from '../grid';

import { AnimateContainer, Container, HiddenBottom, MotionContainer } from './artworksGrid.styles';
import { ANIMATION_DURATION, ARTWORKS_GRID_TEST_ID, ARTWORK_POSITION_TYPE } from './artworksGrid.constants';

export interface ArtworksGridProps {
  artworks: Artwork[];
  gridSize: GRID_SIZE;
  groupingsIntro?: ReactNode;
}

export const ArtworksGrid = ({ artworks, gridSize, groupingsIntro }: ArtworksGridProps) => {
  const [loading, setLoading] = useState(true);
  const { currentPage } = useSetCurrentPage();
  const [newGridSize, setNewGridSize] = useState<GRID_SIZE | null>(null);
  const [oldGridSize, setOldGridSize] = useState<GRID_SIZE | null>(null);
  const [position, setPosition] = useState<ARTWORK_POSITION_TYPE>(ARTWORK_POSITION_TYPE.RELATIVE);
  const [bottomRef, inView] = useInView();
  const [firstFetchLoaded, setFirstFetchLoaded] = useState(false);
  const { isRehydrated } = usePersist();
  const router = useRouter();
  const queryWithoutPage = omit(['page'], router.query);
  const { filterOptions } = useFilters();
  const isEmptyFilters = hasEmptyFilters(filterOptions || [], router.query);
  const { isGroupingRoute, isTradeMemberRoute } = useArtworksRoute();
  const { fetchArtworks, meta: artworksMeta, loading: artworksLoading } = useFetchArtworks();
  const { fetchTradeArtworks, meta: tradeMeta, loading: tradeArtworksLoading } = useFetchTradeArtworks() || {};
  const fetch = isTradeMemberRoute ? fetchTradeArtworks : fetchArtworks;
  const { fetchGroupingArtworks, groupingMeta, groupingLoading } = useFetchGroupingArtworks() || {};
  const currentPLP = getCurrentPLP();
  const allMeta = {
    groupings: groupingMeta,
    trade: tradeMeta,
    artworks: artworksMeta,
  };

  const allLoading = {
    groupings: groupingLoading,
    trade: tradeArtworksLoading,
    artworks: artworksLoading,
  };
  const meta = allMeta[currentPLP];
  const itemsLoading = allLoading[currentPLP];

  const fetchNextArtworks = async (infiniteLoader: boolean, newPage = 2) => {
    try {
      if (itemsLoading) {
        return;
      }
      setLoading(true);
      const query = parse(location.search.replace('?', ''));
      const page = query.page ? +query.page + 1 : newPage;
      const slug = router.query.grouping as string;

      const baseParams = {
        page,
        infiniteLoader,
        filterOptions: omit(['page'], query),
      };

      const hasNextPageParams = {
        page: 2,
        infiniteLoader: true,
        filterOptions: omit(['page'], query),
      };

      if (isGroupingRoute) {
        const { meta } = await fetchGroupingArtworks({
          ...baseParams,
          slug,
        });

        if (!infiniteLoader && meta.hasNext) {
          await fetchGroupingArtworks({ ...hasNextPageParams, slug });
        }
      } else {
        const { meta } = await fetch(baseParams);

        if (!infiniteLoader && meta.hasNext) {
          await fetch(hasNextPageParams);
        }
      }
      setLoading(false);
    } catch (e) {
      reportError(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentPage !== 1 && currentPage === meta.page && meta.hasNext) {
      fetchNextArtworks(true).finally(() => ({}));
    }
  }, [currentPage]);

  useEffect(() => {
    if (inView && !loading && meta.hasNext) {
      fetchNextArtworks(true).finally(() => setLoading(false));
    }
  }, [inView]);

  useDeepCompareEffect(() => {
    if (firstFetchLoaded) {
      fetchNextArtworks(false, 1).finally(() => ({}));
    }
  }, [queryWithoutPage]);

  useEffect(() => {
    if (isRehydrated) {
      if (meta.hasNext) {
        fetchNextArtworks(true).finally(() => ({}));
      }

      setTimeout(() => {
        setOldGridSize(gridSize);
        setLoading(false);
        setFirstFetchLoaded(true);
      }, ANIMATION_DURATION);
    }
  }, [isRehydrated]);

  useEffect(() => {
    setTimeout(() => {
      setNewGridSize(gridSize);
    });
  }, [gridSize]);

  const isReverse = gridSize && oldGridSize && GRID_SIZE_ORDER[gridSize] < GRID_SIZE_ORDER[oldGridSize];
  const motionProps = {
    initial: oldGridSize
      ? {
          scale: isReverse ? 2 : 0.5,
          opacity: 0,
        }
      : false,
    animate: {
      scale: 1,
      opacity: 1,
    },
    exit: {
      scale: isReverse ? 0.5 : 2,
      opacity: 0,
    },
    transition: { ease: 'linear', duration: ANIMATION_DURATION / 1000 },
    onAnimationComplete: () => {
      setPosition(ARTWORK_POSITION_TYPE.RELATIVE);
      setOldGridSize(gridSize);
    },
    onAnimationStart: () => {
      setPosition(ARTWORK_POSITION_TYPE.ABSOLUTE);
    },
  };

  const renderGridSize: (...rest: any) => any = cond([
    [
      equals(GRID_SIZE.SMALL),
      always(
        <MotionContainer key={GRID_SIZE.SMALL} {...motionProps} position={position}>
          <SmallGrid artworks={artworks} />
        </MotionContainer>
      ),
    ],
    [
      equals(GRID_SIZE.MEDIUM),
      always(
        <MotionContainer key={GRID_SIZE.MEDIUM} {...motionProps} position={position}>
          <MediumGrid artworks={artworks} />
        </MotionContainer>
      ),
    ],
    [
      equals(GRID_SIZE.LARGE),
      always(
        <MotionContainer key={GRID_SIZE.LARGE} {...motionProps} position={position}>
          <LargeGrid artworks={artworks} />
        </MotionContainer>
      ),
    ],
  ]);

  const renderArtworksPagination = renderWhenTrue(always(<ArtworksPagination />));

  return (
    <Container data-testid={ARTWORKS_GRID_TEST_ID}>
      {groupingsIntro}
      <AnimateContainer>
        <AnimatePresence>{renderGridSize(newGridSize)}</AnimatePresence>
      </AnimateContainer>
      <NoResults isVisible={!isEmptyFilters && !loading && !artworks.length} />
      <InfiniteLoader isVisible={loading && !!artworks.length} />
      <Element name={HIDDEN_BOTTOM}>
        <HiddenBottom ref={bottomRef} />
      </Element>
      {renderArtworksPagination(!!artworks.length)}
    </Container>
  );
};
